import { createSlice, WritableDraft } from "@reduxjs/toolkit";
import { SeoUrls } from "../../types/index.js";
import { SEOURLNotFoundError, urlToBase64 } from "../../utils/utils.js";
import {
  getSeoUrls,
  patchSeoUrl,
  postSeoCrawler,
  postSeoUrls,
} from "./actions.js";

const initialState: SeoUrls = {
  byId: {},
  allItems: [],
  areLoading: false,
  isImporting: false,
  prevUrlState: {},
};

const importUrls = (state: WritableDraft<SeoUrls>, isImporting: boolean) => {
  state.isImporting = isImporting;
};

const slice = createSlice({
  name: "seo/urls",
  initialState,
  reducers: { clearSeoUrls: () => initialState },
  extraReducers: (builder) => {
    builder.addCase(getSeoUrls.pending, (state, action) => {
      // Delete existing urls if page 1 is requested,
      // which means a tab change in the SEO UI
      if (!action.meta.arg.keepExisting) {
        state = initialState;
      }
      return { ...state, areLoading: true };
    });

    builder.addCase(getSeoUrls.fulfilled, (state, action) => {
      const urls = action.payload;
      urls.forEach((url) => {
        const key = urlToBase64(url.url);

        // Append the newly recieved URLs if not already present
        if (state.allItems.indexOf(key) === -1) {
          state.allItems.push(key);
        }

        state.byId[key] = url;
      });

      state.areLoading = false;
    });

    builder.addCase(patchSeoUrl.pending, (state, action) => {
      const { urlId, request } = action.meta.arg;
      const existing = state.byId[urlId];
      if (!existing) throw new SEOURLNotFoundError(urlId);

      state.prevUrlState[action.meta.requestId] = { ...existing };
      state.byId[urlId] = { ...existing, ...request };
    });

    builder.addCase(patchSeoUrl.fulfilled, (state, action) => {
      delete state.prevUrlState[action.meta.requestId];
    });

    builder.addCase(patchSeoUrl.rejected, (state, action) => {
      const { urlId } = action.meta.arg;
      if (action.meta.condition) {
        throw new SEOURLNotFoundError(urlId);
      }

      const { requestId } = action.meta;
      const prevState = state.prevUrlState[requestId];
      if (!prevState) return;

      delete state.prevUrlState[requestId];
      state.byId[urlId] = { ...prevState };
    });

    builder.addCase(postSeoUrls.pending, (state) => importUrls(state, true));
    builder.addCase(postSeoUrls.fulfilled, (state) => importUrls(state, false));
    builder.addCase(postSeoUrls.rejected, (state) => importUrls(state, false));

    builder.addCase(postSeoCrawler.fulfilled, () => initialState);
  },
});

export const { clearSeoUrls } = slice.actions;

export default slice.reducer;
